// src/components/captain/CaptainDashboard.jsx
import { useState, useMemo, useEffect } from "react";
import { Compass, CircleDollarSign, Car, UserCircle } from "lucide-react";
import toast from "react-hot-toast";
import AppShell from "../layout/AppShell";
import TripCard from "../trips/TripCard";
import { Card, Button, Stars, Avatar, StatCard, EmptyState, Spinner, T } from "../ui";
import { useAuth } from "../../contexts/AuthContext";
import { usePendingTrips, useCaptainTrips } from "../../hooks/useTrips";
import { useSettings } from "../../contexts/SettingsContext";
import { timeLeftMins } from "../../utils/helpers";

export default function CaptainDashboard() {
  const { user, refreshUser } = useAuth();
  const settings = useSettings();
  const [tab, setTab] = useState("available");
  const { data: pending, loading: pLoad } = usePendingTrips();
  const { data: myTrips, loading: mLoad } = useCaptainTrips(user?.id);

  // --- الجزء السحري للاستجابة اللحظية ---
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    // تحديث الحالة كل دقيقة لإعادة حساب التوقيت وإخفاء الرحلات المنتهية فوراً
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 10000); // تحديث كل 10 ثوانٍ لضمان الدقة اللحظية

    return () => clearInterval(interval);
  }, []);
  // ---------------------------------------

  // دالة الفلترة مع دمج منطق الوقت
  const { availableTrips, offeredTrips } = useMemo(() => {
    const filteredAvailable = [];
    const filteredOffered = [];

    pending.forEach(t => {
      // حساب الوقت المتبقي بناءً على اللحظة الحالية (now)
      const searchLeft = t.status === "pending" && t.createdAt
        ? timeLeftMins(t.createdAt, settings.searchLimitMinutes)
        : null;

      // هل الرحلة صالحة؟ (وقت البحث لم ينتهِ)
      const isExpired = searchLeft !== null && searchLeft <= 0;

      if (!isExpired) {
        const hasOffer = t.offers?.some(o => o.captainId === user?.id);
        if (hasOffer) {
          filteredOffered.push(t);
        } else {
          filteredAvailable.push(t);
        }
      }
    });

    return { availableTrips: filteredAvailable, offeredTrips: filteredOffered };
  }, [pending, user?.id, settings.searchLimitMinutes, now]); // أضفنا now هنا لضمان التحديث اللحظي

  const tabs = [
    {
      key: "available",
      label: "المتاحة",
      icon: <Compass size={22} strokeWidth={2.5} color="#0EA5E9" fill="#0EA5E922" />,
      badge: availableTrips.length > 0 ? availableTrips.length : null
    },
    {
      key: "offered",
      label: "عروضي",
      icon: <CircleDollarSign size={22} strokeWidth={2.5} color="#F59E0B" fill="#F59E0B22" />,
      badge: offeredTrips.length > 0 ? offeredTrips.length : null
    },
    {
      key: "mytrips",
      label: "مشاويري",
      icon: <Car size={22} strokeWidth={2.5} color="#10B981" fill="#10B98122" />
    },
    {
      key: "profile",
      label: "ملفي",
      icon: <UserCircle size={22} strokeWidth={2.5} color="#8B5CF6" fill="#8B5CF622" />
    },
  ];

  return (
    <AppShell tabs={tabs} activeTab={tab} onTabChange={setTab}>
      {tab === "available" && (
        <>
          <h2 style={{ margin: "0 0 18px", fontSize: 20, fontWeight: 800 }}>
            الرحلات المتاحة <span style={{ fontSize: 14, color: T.muted, fontWeight: 400 }}>({availableTrips.length})</span>
          </h2>
          {pLoad ? <Spinner /> : availableTrips.length === 0
            ? <EmptyState icon="🗺️" title="لا توجد رحلات متاحة" sub="ستظهر هنا الرحلات الجديدة من العملاء" />
            : availableTrips.map(t => <TripCard key={t.id} trip={t} />)
          }
        </>
      )}

      {tab === "offered" && (
        <>
          <h2 style={{ margin: "0 0 18px", fontSize: 20, fontWeight: 800 }}>عروضي المعلقة</h2>
          {offeredTrips.length === 0
            ? <EmptyState icon="💰" title="لا توجد عروض معلقة" />
            : offeredTrips.map(t => <TripCard key={t.id} trip={t} />)
          }
        </>
      )}

      {tab === "mytrips" && (
        <>
          <h2 style={{ margin: "0 0 18px", fontSize: 20, fontWeight: 800 }}>مشاويري</h2>
          {mLoad ? <Spinner /> : myTrips.length === 0
            ? <EmptyState icon="🚗" title="لا توجد مشاوير بعد" sub="ستظهر هنا رحلاتك المؤكدة والمكتملة" />
            : myTrips.map(t => <TripCard key={t.id} trip={t} />)
          }
        </>
      )}

      {tab === "profile" && <CaptainProfile user={user} refreshUser={refreshUser} />}
    </AppShell>
  );
}
function CaptainProfile({ user, refreshUser }) {
  const [carFiles, setCarFiles] = useState([null, null]);
  const [uploading, setUploading] = useState(false);

  const save = async () => {
    const toUp = carFiles.filter(Boolean);
    if (!toUp.length) { toast.error("يرجى اختيار صورة واحدة على الأقل"); return; }
    setUploading(true);
    try {
      const urls = await uploadFiles(toUp, `captains/${user.id}/car`);
      const merged = [...(user.carPhotoUrls || []), ...urls];
      await updateCaptainCarPhotos(user.id, merged);
      await refreshUser();
      toast.success("✓ تم حفظ صور السيارة");
      setCarFiles([null, null]);
    } catch (e) { toast.error(e.message); }
    setUploading(false);
  };

  return (
    <>
      <Card style={{ textAlign: "center", marginBottom: 16 }}>
        {user?.photoUrl
          ? <img src={user.photoUrl} alt="" style={{ width: 76, height: 76, borderRadius: "50%", objectFit: "cover", margin: "0 auto" }} />
          : <div style={{ margin: "0 auto" }}><Avatar name={user?.name} size={76} color={T.teal} /></div>
        }
        <div style={{ marginTop: 12, fontSize: 20, fontWeight: 800 }}>{user?.name}</div>
        <Stars rating={user?.rating || 0} size={16} />
        <div style={{ fontSize: 13, color: T.muted, marginTop: 6 }}>{user?.car}</div>
        <div style={{ fontSize: 12, color: T.muted }}>{user?.plate} • {user?.phone}</div>
      </Card>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
        <StatCard label="رحلة" value={user?.trips || 0} icon="🏁" color={T.info} />
        <StatCard label="تقييم" value={Number(user?.rating || 0).toFixed(1)} icon="⭐" color={T.gold} />
        <StatCard label="رصيد ج.م" value={user?.balance || 0} icon="💰" color={T.success} />
      </div>

      <Card>
        <h4 style={{ margin: "0 0 12px", fontWeight: 700 }}>🚗 صور السيارة الحالية</h4>
        {(user?.carPhotoUrls || []).length > 0 ? (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
            {user.carPhotoUrls.map((url, i) => (
              <img key={i} src={url} alt="" style={{ width: "100%", borderRadius: 10, height: 110, objectFit: "cover", background: T.surfAlt }} />
            ))}
          </div>
        ) : <p style={{ color: T.muted, fontSize: 13, marginBottom: 14 }}>لم يتم رفع صور بعد</p>}

        <h4 style={{ margin: "0 0 10px", fontWeight: 700 }}>➕ إضافة صور</h4>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
          <ImageUpload label="أمامية" value={carFiles[0]} onChange={f => setCarFiles([f, carFiles[1]])} />
          <ImageUpload label="خلفية" value={carFiles[1]} onChange={f => setCarFiles([carFiles[0], f])} />
        </div>
        <Button full loading={uploading} onClick={save}>حفظ الصور</Button>
      </Card>
    </>
  );
}
